import api from './api';

function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  // Use the main service worker (registered by serviceWorkerRegistration.ts)
  // which includes both PWA caching and push notification handlers
  const registration = await navigator.serviceWorker.ready;

  // Get VAPID public key
  const { data } = await api.get('/push/vapid-key');
  if (!data.publicKey) return false;

  // Subscribe
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.publicKey),
  });

  const json = subscription.toJSON();

  // Save to backend
  await api.post('/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    },
  });

  return true;
}

export async function unsubscribeFromPush(): Promise<void> {
  const subscription = await getExistingSubscription();
  if (!subscription) return;

  // Unsubscribe from browser
  await subscription.unsubscribe();

  // Remove from backend
  await api.delete('/push/unsubscribe', {
    data: { endpoint: subscription.endpoint },
  });
}
